import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  MemberSearchApi,
  type MemberTxnDetail,
} from "@/api/giftcard.api";
import { mapVerifiedMemberDetails } from "../mapVerifiedMember";
import { mapActiveCouponsToItems } from "../mapMemberRewards";
import { benefits } from "../mockData";
import type { CouponItem, LedgerRow, MemberProfile, MemberStat } from "../types";
import { BenefitCard } from "./BenefitCard";
import { CouponCard } from "./CouponCard";
import { LedgerTable } from "./LedgerTable";
import { MemberDashboardSkeleton } from "./MemberDashboardSkeleton";
import { MemberHeader } from "./MemberHeader";
import { ProfileCard } from "./ProfileCard";
import { StatCard } from "./StatCard";

function getAvatarInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "M";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function formatInvoiceAmt(value: number | string | undefined) {
  if (value === undefined || value === null || value === "") return "0";
  const numeric =
    typeof value === "number"
      ? value
      : Number(String(value).replace(/[^\d.-]/g, ""));
  if (Number.isNaN(numeric)) return "0";
  return numeric.toLocaleString("en-IN");
}

function mapTxnDetailsToLedgerRows(txns: MemberTxnDetail[] = []): LedgerRow[] {
  return txns.map((txn, index) => ({
    id: `${txn.invoiceNo ?? "txn"}-${txn.counterId ?? index}`,
    counterId: txn.counterId ?? "—",
    invoiceNo: txn.invoiceNo ?? "—",
    transactionType: txn.transactionType ?? "—",
    invoiceAmt: formatInvoiceAmt(txn.invoiceAmt),
  }));
}

interface MemberDashboardProps {
  initialProfile: MemberProfile;
  initialStats: MemberStat[];
}

export function MemberDashboard({
  initialProfile,
  initialStats,
}: MemberDashboardProps) {
  const [member, setMember] = useState<MemberProfile>(initialProfile);
  const [stats, setStats] = useState<MemberStat[]>(initialStats);
  const [ledgerRows, setLedgerRows] = useState<LedgerRow[]>([]);
  const [activeCoupons, setActiveCoupons] = useState<CouponItem[]>([]);
  const [isLoadingTxns, setIsLoadingTxns] = useState(true);
  const [isLoadingMemberDetails, setIsLoadingMemberDetails] = useState(true);

  useEffect(() => {
    const mobileNo = initialProfile.mobile?.replace(/\D/g, "");
    if (!mobileNo) {
      setIsLoadingTxns(false);
      setIsLoadingMemberDetails(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setIsLoadingTxns(true);
      try {
        const data = await MemberSearchApi.getTransactionsByMobileNo({
          p_MobileNo: mobileNo,
        });
        if (cancelled) return;
        setLedgerRows(mapTxnDetailsToLedgerRows(data?.txnDetails));
      } catch (err) {
        if (!cancelled) {
          console.error("GetTransactionsByMobileNo failed:", err);
          setLedgerRows([]);
        }
      } finally {
        if (!cancelled) setIsLoadingTxns(false);
      }
    })();

    (async () => {
      setIsLoadingMemberDetails(true);
      try {
        const data = await MemberSearchApi.getMemberDetails({
          p_MobileNo: mobileNo,
        });
        if (cancelled) return;

        const detail = data?.newMemberDetails?.[0];
        if (detail) {
          const mapped = mapVerifiedMemberDetails(
            detail,
            mobileNo,
            initialProfile
          );
          setMember(mapped.profile);
          setStats(mapped.stats);
        }

        setActiveCoupons(mapActiveCouponsToItems(data?.activeCoupons));
      } catch (err) {
        if (!cancelled) {
          console.error("GetMemberDetails failed:", err);
        }
      } finally {
        if (!cancelled) setIsLoadingMemberDetails(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [initialProfile.mobile]);

  if (isLoadingMemberDetails) {
    return <MemberDashboardSkeleton />;
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.35 }}
      className="mx-auto max-w-6xl space-y-8 px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-12"
    >
      <MemberHeader member={member} />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat, index) => (
          <StatCard key={stat.id} stat={stat} index={index} />
        ))}
      </div>

      <LedgerTable rows={ledgerRows} isLoading={isLoadingTxns} />

      <section className="space-y-6 rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">
            My Rewards
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Redeem points and exclusive member perks
          </p>
        </div>

        <div className="rounded-2xl border border-amber-100 bg-gradient-to-br from-amber-50/70 via-white to-slate-50 p-5 sm:p-6">
          <h3 className="text-xs font-bold uppercase tracking-[0.14em] text-amber-600 sm:text-sm">
            Member Benefits
          </h3>
          <ul className="mt-4 space-y-3.5">
            {benefits.map((benefit) => (
              <BenefitCard key={benefit.id} benefit={benefit} />
            ))}
          </ul>
        </div>
      </section>

      <section className="space-y-5">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">
            Active Coupons
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Ready-to-use offers on your account
          </p>
        </div>
        {activeCoupons.length === 0 ? (
          <p className="rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-500">
            No active coupons available
          </p>
        ) : (
          <div className="grid gap-4 lg:grid-cols-1 xl:grid-cols-1">
            {activeCoupons.map((coupon) => (
              <CouponCard key={coupon.id} coupon={coupon} />
            ))}
          </div>
        )}
      </section>

      <ProfileCard
        member={member}
        onSave={(updates) => {
          setMember((prev) => ({
            ...prev,
            ...updates,
            avatarInitials: getAvatarInitials(updates.name || prev.name),
          }));
        }}
      />
    </motion.div>
  );
}
